import Animation from 'animation'
import * as d3 from 'd3-selection'
import * as util from '../shape-util'
import * as geo from 'd3-geo'
import intersect from 'path-intersection'
import Tooltip from 'tooltip'
import tooltipContents from 'tooltip/contents'

class FilledMapBasicAnimation extends Animation {

  match (serie) {
    return serie.type === 'filled-map'
  }

  configure (axis, serie) {
    super.configure(axis, serie)

    this.lassoDrag = this.lassoDrag.bind(this)
    this.lassoEnd = this.lassoEnd.bind(this)
    this._clearTooltip = this._clearTooltip.bind(this)

    let node = this.chart.layout.axes.map.node

    if (node.children) {
      // Only worry about lasso events if the node has children
      // If it doesn't have children, it's a leaf node so there's nothing to select
      this.chart.ee.on('lasso:drag', this.lassoDrag)
      this.chart.ee.on('lasso:end', this.lassoEnd)
    }

    return this
  }

  lassoDrag (data) {
    let bb = data.box

    this.features.classed('selected', false)
                 .filter(feature => util.intersect(feature.boundingBox, bb)) // Fast way to get a subset of paths based on bounding box
                 .filter(function (feature) {
                   if (util.pointInBox(feature.boundingBox[0], bb) &&
                     util.pointInBox(feature.boundingBox[1], bb)) {
                     // The top left and bottom right points of this feature's bounding box are inside the lasso,
                     // so we can safely assume the entire feature is within the lasso
                     return true
                   }

                   // Check if any of the 4 corners of the lasso area are in this feature
                   let contains = data.coordinates.find(coord => geo.geoContains(feature, coord))
                   if (contains) {
                     return true
                   }

                   return intersect(data.path, this.getAttribute('d')).length
                 })
                 .classed('selected', true)
  }

  lassoEnd (data, dragged) {
    let selected = this.features
                       .filter('.selected')
                       .classed('selected', false)
      , features = selected.data()

    if (!dragged && features.length) {
      // A click event on the feature will trigger the zoom to, do nothing
      return
    }
    this.chart.ee.emit('chart-event', 'map:zoomTo', features)
  }

  bindAndAnimate (container, node) {
    let serie = d3.select(node).datum()
      , config = this.chart.datum().axes.map
      , treeNode = config.node
      , children = treeNode.children
      , map = container.selectAll('g.map:first-child').selection()

    this.chart.ee.on('globe:rotate', this._clearTooltip)

    // Used to store the tooltip object so it can be destroyed by hover code.
    let hoverContainer = container.selectAll('g.hover-container').selection()
                                  .data(d => [d])
                                  .enter()
                                    .append('g').attr('class', 'hover-container')
                                    .merge(container.selectAll('g.hover-container'))
    // Look at the map
    this.features = map.selectAll('.feature:not(.hover-border)')
                       .select(function (d) {
                         let point = serie.points.find(point => point.nodeKey == d.id)

                         d3.select(this)
                           .style('fill', point?.color)
                           .classed('deemphasize', point ? point.deemphasize : config.deemphasize) // If we have a point, use its `deemphasize`. Otherwise default to the config level deemphasize for uncolored features
                         return this
                       })
                       .selection()

    this.features.on('mouseenter.filled-mouse', (e, d) => {
                    if (map.classed('globe-spinning')) {
                      return // Ignore
                    }
                    if (!this._tooltip) {
                      this._tooltip = new Tooltip(d3.select(this.chart.node().parentNode).attr('id') + '-tooltip')
                    }
                    if (!serie.points.length) {
                      return
                    }
                    let svg = this.chart.node().ownerSVGElement
                      , scale = svg.getBoundingClientRect().width / parseInt(d3.select(svg).style('width'))
                      , path = map.datum().path // Grab the path on mouseenter in case an animatation updated the projection on the `end` event. See Impact #49320
                      , margin = this.chart.datum().dimensions.margin
                      , box = {
                        left: svg.getBoundingClientRect().left + margin.left,
                        top: svg.getBoundingClientRect().top + margin.top
                      }
                      , formatter = serie.formatter || (d => d)
                      , centroidNode = children ? children.find(c => c.key == d.id) : treeNode
                      , rows = [{
                        cols: [{
                          text: formatter(serie.points.find(p => {
                            return p.nodeKey == d.id
                          }).raw)
                        }],
                        header: [centroidNode.label]
                      }]
                      , centroid = centroidNode.centroid && path.projection()(centroidNode.centroid)

                    if (!centroid) {
                      // We don't know where to position the tooltip
                      return
                    }

                    hoverContainer.node().__tooltip = this._tooltip
                    this._tooltip._html(tooltipContents(rows, this.chart))
                                ._position(box.left + centroid[0] * scale, box.top + centroid[1] * scale)
                                .show()
                  })
                  .on('mouseleave', this._clearTooltip)
  }

  _clearTooltip () {
    if (this._tooltip) {
      this._tooltip.destroy()
      this._tooltip = null
    }
  }

}

export default FilledMapBasicAnimation
